/**
 * PREVIEW CHAT
 *
 * AI chat sidebar for the Preview page, styled with Colony design system.
 * Two modes:
 * - Cloud: stub replies (placeholder)
 * - Local: real AI chat via CLI providers through Tauri backend
 */

import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTimeoutRef } from "@/hooks/useTimeoutRef";
import { Send, ChevronDown, CornerDownLeft } from "lucide-react";
import { useLocalPreviewStore } from "@/stores/localPreviewStore";
import { useWorldStore } from "@/stores/worldStore";
import type { Project } from "@/stores/worldStore";
import type { AiProvider } from "@/types/ai-chat";
import { ClaudeLogo, CodexLogo, GeminiLogo } from "@/components/icons/AILogos";
import { resolveProjectPath } from "./projectPath";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface PreviewChatProps {
  isLocalMode?: boolean;
  selectedProject?: Project;
}

// ─── Provider config ──────────────────

const PROVIDERS: { id: AiProvider; label: string; Logo: React.FC<{ size?: number }> }[] = [
  { id: "claude-code", label: "Claude Code", Logo: ClaudeLogo },
  { id: "codex", label: "Codex", Logo: CodexLogo },
  { id: "gemini", label: "Gemini", Logo: GeminiLogo },
];

const INITIAL_MESSAGES: Message[] = [
  {
    id: "1",
    role: "assistant",
    content: "Hi! I'm your AI assistant. I can help you with this project \u2014 ask me anything about the code, suggest changes, or request a feature.",
  },
  {
    id: "2",
    role: "user",
    content: "Can you add a dark mode toggle to the settings page?",
  },
  {
    id: "3",
    role: "assistant",
    content: "Sure! I'll add a dark mode toggle to the settings page. I'll create a `ThemeToggle` component that uses your existing theme context and wire it into the settings layout. Want me to proceed?",
  },
];

const STUB_REPLIES = [
  "I'll look into that. Give me a moment to analyze the codebase...",
  "Great idea! I can implement that. Let me draft the changes.",
  "I see what you mean. I'll make those adjustments to the component.",
  "That's an interesting approach. Let me check the existing implementation first.",
  "Done! I've applied the changes. Check the preview to see the result.",
];

// ── Cloud Chat (stub) ────────────────────────────────────────────────

function CloudChat() {
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [provider, setProvider] = useState<AiProvider>("claude-code");
  const typingTimerRef = useTimeoutRef();

  const sendMessage = () => {
    const text = input.trim();
    if (!text || isTyping) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    typingTimerRef.current = setTimeout(() => {
      const reply = STUB_REPLIES[Math.floor(Math.random() * STUB_REPLIES.length)];
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: reply,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <ChatUI
      messages={messages}
      input={input}
      setInput={setInput}
      onSend={sendMessage}
      onKeyDown={handleKeyDown}
      isTyping={isTyping}
      placeholder="Type your message..."
      provider={provider}
      setProvider={setProvider}
    />
  );
}

// ── Local Chat (real AI) ─────────────────────────────────────────────

interface LocalChatProps {
  selectedProject?: Project;
}

function LocalChat({ selectedProject }: LocalChatProps) {
  const {
    chatMessages,
    chatStreaming,
    sendChatMessage,
    serverPort,
  } = useLocalPreviewStore();
  const projects = useWorldStore((s) => s.projects);
  const [input, setInput] = useState("");
  const [provider, setProvider] = useState<AiProvider>("claude-code");

  const localProject = useMemo(
    () => projects.find((p) => p.localPath),
    [projects]
  );
  const projectPath =
    resolveProjectPath(selectedProject) ||
    resolveProjectPath(localProject);
  const previewUrl = serverPort ? `http://localhost:${serverPort}` : undefined;

  const sendMessage = () => {
    const text = input.trim();
    if (!text || chatStreaming || !projectPath) return;

    setInput("");
    void sendChatMessage(text, projectPath, previewUrl, provider);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Map store messages to UI format
  const messages: Message[] = chatMessages.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
  }));

  return (
    <ChatUI
      messages={messages}
      input={input}
      setInput={setInput}
      onSend={sendMessage}
      onKeyDown={handleKeyDown}
      isTyping={chatStreaming}
      placeholder="Ask about your project..."
      statusLabel={chatStreaming ? "Thinking" : "Online"}
      provider={provider}
      setProvider={setProvider}
    />
  );
}

// ── Shared Chat UI (Colony Design) ───────────────────────────────────

export interface ChatUIProps {
  messages: Message[];
  input: string;
  setInput: (v: string) => void;
  onSend: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  isTyping: boolean;
  placeholder?: string;
  statusLabel?: string;
  provider?: AiProvider;
  setProvider?: (provider: AiProvider) => void;
}

export function ChatUI({
  messages,
  input,
  setInput,
  onSend,
  onKeyDown,
  isTyping,
  placeholder = "Type your message...",
  statusLabel = "Online",
  provider = "claude-code",
  setProvider,
}: ChatUIProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [providerOpen, setProviderOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const selectedProvider = PROVIDERS.find((p) => p.id === provider) ?? PROVIDERS[0];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Close provider dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setProviderOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col h-full bg-colony-surface">
      {/* ── Header ── */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-colony-border bg-[var(--background)] shrink-0">
        <div className="flex items-center gap-2">
          <img src="/colony-logo.png" alt="Colony" className="w-4 h-4 rounded-sm object-contain" />
          <span className="text-[13px] font-semibold text-colony-text-primary">
            Colony AI
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Provider selector */}
          {setProvider && (
            <div ref={dropRef} className="relative">
              <button
                onClick={() => setProviderOpen(!providerOpen)}
                className="flex items-center gap-1.5 px-2 h-6 rounded-[4px] bg-colony-surface-elevated border border-colony-border hover:border-colony-border-hover cursor-pointer transition-colors"
              >
                <selectedProvider.Logo size={14} />
                <span className="text-[12px] font-medium text-colony-text-primary">
                  {selectedProvider.label}
                </span>
                <ChevronDown size={12} className="text-colony-text-tertiary" />
              </button>
              {providerOpen && (
                <div className="absolute top-full right-0 mt-1 z-50 min-w-[160px] py-1 rounded-[8px] border border-colony-border bg-colony-surface shadow-[var(--shadow-elevated)]">
                  {PROVIDERS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setProvider(p.id); setProviderOpen(false); }}
                      className={`w-full flex items-center gap-2 px-2.5 h-8 text-[12px] hover:bg-colony-surface-elevated transition-colors cursor-pointer ${
                        p.id === provider ? "text-colony-brand" : "text-colony-text-primary"
                      }`}
                    >
                      <p.Logo size={14} />
                      <span>{p.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status badge */}
          <div className={`flex items-center gap-1.5 px-2 h-6 rounded-[4px] border ${
            isTyping
              ? "bg-colony-amber/10 text-colony-amber border-colony-amber/20"
              : "bg-colony-green/10 text-colony-green border-colony-green/20"
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${
              isTyping ? "bg-colony-amber animate-pulse" : "bg-colony-green"
            }`} />
            <span className="text-[10px] font-medium uppercase tracking-wider">
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5" ref={scrollRef} style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-colony-brand/10 border border-colony-brand/20 flex items-center justify-center">
              <img src="/colony-logo.png" alt="Colony" className="w-6 h-6 rounded-sm object-contain" />
            </div>
            <p className="text-[13px] text-colony-text-secondary">
              Ask me anything about your project.
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
            {/* Avatar — only for AI */}
            {msg.role === "assistant" && (
              <div className="w-6 h-6 rounded-[4px] bg-colony-brand/10 flex items-center justify-center shrink-0 mt-0.5">
                <img src="/colony-logo.png" alt="Colony" className="w-4 h-4 rounded-sm object-contain" />
              </div>
            )}
            <div className={`flex-1 min-w-0 ${msg.role === "user" ? "flex flex-col items-end" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] font-medium text-colony-text-primary">
                  {msg.role === "assistant" ? "Colony AI" : "You"}
                </span>
                {msg.role === "assistant" && (
                  <span className="text-[10px] text-colony-text-tertiary">Just now</span>
                )}
              </div>
              {msg.role === "user" ? (
                <div className="inline-block px-3 py-2 rounded-[8px] bg-colony-brand/15 text-colony-text-primary text-[13px] leading-relaxed max-w-[85%]">
                  {msg.content}
                </div>
              ) : (
                <div className="text-[13px] text-colony-text-secondary leading-relaxed max-w-[95%]">
                  {msg.content}
                  {isTyping && msg === messages[messages.length - 1] && (
                    <span className="inline-block w-1.5 h-4 bg-colony-text-tertiary animate-pulse ml-0.5 align-text-bottom rounded-sm" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {isTyping && (messages.length === 0 || messages[messages.length - 1]?.role !== "assistant") && (
          <div className="flex gap-3">
            <div className="w-6 h-6 rounded-[4px] bg-colony-brand/10 flex items-center justify-center shrink-0 mt-0.5">
              <img src="/colony-logo.png" alt="Colony" className="w-4 h-4 rounded-sm object-contain" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] font-medium text-colony-text-primary">Colony AI</span>
              </div>
              <div className="flex items-center gap-1 h-6">
                <span className="w-1.5 h-1.5 rounded-full bg-colony-text-tertiary animate-pulse" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-colony-text-tertiary animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-colony-text-tertiary animate-pulse" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Input ── */}
      <div className="p-4 bg-[var(--background)]">
        <div className="relative flex items-end gap-2 p-3 rounded-[8px] bg-[#151518] border border-colony-border focus-within:border-colony-brand/50 focus-within:shadow-[0_0_0_2px_rgba(0,178,200,0.1)] transition-all">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              const ta = e.target;
              ta.style.height = "auto";
              ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full bg-transparent text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary resize-none outline-none py-2 px-1 min-h-[56px] max-h-[120px]"
            rows={2}
            style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}
          />
          <button
            onClick={onSend}
            disabled={!input.trim() || isTyping}
            className={`p-1.5 rounded-[6px] transition-all shrink-0 mb-0.5 cursor-pointer ${
              input.trim() && !isTyping
                ? "bg-colony-brand text-white hover:bg-colony-brand-hover shadow-sm"
                : "bg-colony-surface-elevated text-colony-text-tertiary cursor-not-allowed"
            }`}
          >
            <Send size={14} strokeWidth={2} />
          </button>
        </div>
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-[10px] text-colony-text-tertiary">
            Attach context
          </span>
          <div className="flex items-center gap-1 text-[10px] text-colony-text-tertiary">
            <CornerDownLeft size={10} strokeWidth={1.5} />
            <span>Enter to send</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Exported Component ───────────────────────────────────────────────

export function PreviewChat({ isLocalMode, selectedProject }: PreviewChatProps) {
  if (isLocalMode) {
    return <LocalChat selectedProject={selectedProject} />;
  }
  return <CloudChat />;
}
